import type { StatusUpdateCadence } from './detectStatusUpdateCadence.ts';

/**
 * Keyword catalogues for status-update signal detection.
 *
 * These are matching inputs, not user-facing copy, and are deliberately
 * English-only: the nudges they gate open an English Rovo conversation that
 * builds an English automation rule, so a localized trigger would surface a
 * nudge whose entire downstream experience is still English.
 *
 * Previously defined with `defineMessages` so they could be localized over
 * time. That is no longer the intent, so they are plain constants and are no
 * longer extracted for translation.
 *
 * Matched whole-word and case-insensitively - see `matchesAnyKeyword`. Exported
 * frozen so the compiled-regex cache in `matchesAnyKeyword` (keyed on array
 * identity) stays warm across calls.
 */

/** Title keywords that on their own identify a status-update page. */
export const STATUS_UPDATE_TITLE_KEYWORDS: readonly string[] = Object.freeze([
	'status update',
	'status report',
	'project update',
	'progress update',
	'team update',
	'week in review',
	'weekly recap',
]);

/** Parent page/folder titles that suggest their children are status updates. */
export const STATUS_UPDATE_PARENT_KEYWORDS: readonly string[] = Object.freeze([
	'status updates',
	'weekly updates',
	'monthly',
	'updates',
	'weekly recaps',
	'monthly recaps',
]);

/**
 * Cadence adjectives grouped by the cadence they resolve to. Consumed by
 * `detectStatusUpdateCadence` for the mapping and flattened into
 * `CADENCE_KEYWORDS` for plain recurrence matching.
 */
export const CADENCE_KEYWORDS_BY_CADENCE: Readonly<Record<StatusUpdateCadence, readonly string[]>> =
	Object.freeze({
		daily: ['daily'],
		weekly: ['weekly'],
		biweekly: ['biweekly', 'bi-weekly', 'fortnightly'],
		monthly: ['monthly'],
		quarterly: ['quarterly'],
	});

/** Every cadence adjective as a flat list. */
export const CADENCE_KEYWORDS: readonly string[] = Object.freeze(
	Object.values(CADENCE_KEYWORDS_BY_CADENCE).flat(),
);

/**
 * The bare "week" token. Also used by `parseTitleDate` to recognise "Week 24"
 * style title dates, which is why it is exported separately.
 */
export const WEEK_KEYWORD = 'week';

/** Extra recurrence words that are not cadence adjectives. */
export const RECURRENCE_KEYWORDS: readonly string[] = Object.freeze([WEEK_KEYWORD, 'end of week']);

/** Update words paired (AND) with a recurrence indicator to detect a series. */
export const STATUS_UPDATE_WORD_KEYWORDS: readonly string[] = Object.freeze([
	'update',
	'status',
	'recap',
	'report',
]);

const buildMonthEntries = (): readonly { name: string; index: number }[] => {
	const long = new Intl.DateTimeFormat('en-US', { month: 'long', timeZone: 'UTC' });
	const short = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' });
	const entries: { name: string; index: number }[] = [];
	for (let index = 0; index < 12; index++) {
		const date = new Date(Date.UTC(2021, index, 1));
		entries.push({ name: long.format(date), index });
		entries.push({ name: short.format(date), index });
	}
	return entries;
};

const MONTH_ENTRIES = buildMonthEntries();

/** English long and short month names (e.g. "August", "Aug"). */
export const MONTH_NAMES: readonly string[] = Object.freeze(
	MONTH_ENTRIES.map((entry) => entry.name),
);

/** Maps each lower-cased English month name to its zero-based month index. */
export const MONTH_INDEX_BY_NAME: ReadonlyMap<string, number> = new Map(
	MONTH_ENTRIES.map((entry) => [entry.name.toLowerCase(), entry.index]),
);

/**
 * Recurrence indicators: cadence adjectives, extra recurrence words and month
 * names. Paired (AND) with `STATUS_UPDATE_WORD_KEYWORDS` to detect a recurring
 * status-update title.
 */
export const RECURRENCE_INDICATOR_KEYWORDS: readonly string[] = Object.freeze([
	...CADENCE_KEYWORDS,
	...RECURRENCE_KEYWORDS,
	...MONTH_NAMES,
]);
